import { ApiResponseType } from './types.ts';
import { ApiResponse } from '../../interface/ApiResponse.ts';

/**
 * Parses a raw response body once, centrally. The addon does not always send
 * a JSON content type, so every body is tried as JSON: objects, arrays and
 * JSON scalars (including `null`) come back parsed, anything else stays the
 * raw text. Components never parse payloads themselves.
 */
export const parseResponseBody = (text:string):any => {
    const trimmed = text.trim();
    if (trimmed === '') {
        return text;
    }
    try {
        return JSON.parse(trimmed);
    } catch (err) {
        return text;
    }
};

/** The payload as an array; anything else (object, text, null) becomes an empty array. */
export const asArray = <T = any>(payload:unknown):Array<T> => (Array.isArray(payload) ? payload as Array<T> : []);

/** The payload as a plain object; arrays, text and null become an empty object. */
export const asObject = <T = Record<string, any>>(payload:unknown):T => (
    (payload !== null && typeof payload === 'object' && !Array.isArray(payload)) ? payload as T : {} as T
);

/**
 * An error response whose body is not JSON (a PHP error page, an empty body)
 * still gets a readable error object, so ApiCheck and the message handlers
 * have something to show instead of treating `{}` as "no error".
 */
const asErrorObject = (payload:unknown):Record<string, any> => {
    const parsed = asObject(payload);
    if (Object.keys(parsed).length > 0) {
        return parsed;
    }
    return {
        error: 'invalid_response',
        message: 'Der Server hat eine ungültige Antwort geliefert. Bitte versuchen Sie es später erneut.',
    };
};

/**
 * For list endpoints: a successful response carries an array payload (empty
 * when there is nothing, so check `.length`), an error response carries the
 * error object ({ error, message, errors }).
 */
export const withArrayPayload = (response:ApiResponse):ApiResponse => ({
    type: response.type,
    payload: response.type === ApiResponseType.OK ? asArray(response.payload) : asErrorObject(response.payload),
});

/** For object endpoints and all writes: the payload is always a plain object. */
export const withObjectPayload = (response:ApiResponse):ApiResponse => ({
    type: response.type,
    payload: response.type === ApiResponseType.OK ? asObject(response.payload) : asErrorObject(response.payload),
});
